import type {
  CreateDocumentInput,
  CreateSignedDocumentInput,
  DocumentListInput,
  DocumentRepository,
  TenantContext,
} from '@zetyar/data-contracts'
import type { Document, DocumentCategory, UUID } from '@zetyar/domain'
import type { SupabaseClient } from '@supabase/supabase-js'

// Private bucket created in 0037_document_library.sql.
const DOCUMENTS_BUCKET = 'org-documents'
const SIGNED_URL_SECONDS = 60

interface DocumentRow {
  readonly id: UUID
  readonly organization_id: UUID
  readonly title: string
  readonly description: string | null
  readonly file_url: string
  readonly file_name: string
  readonly file_size: number
  readonly mime_type: string
  readonly version: string
  readonly created_at: string
  readonly updated_at: string
  readonly category: { readonly id: UUID; readonly name: string } | null
}

interface CategoryRow {
  readonly id: UUID
  readonly organization_id: UUID
  readonly name: string
  readonly description: string | null
  readonly created_at: string
}

function mapCategory(row: CategoryRow): DocumentCategory {
  return {
    id: row.id,
    organizationId: row.organization_id,
    name: row.name,
    description: row.description,
    parentId: null,
    sortOrder: 0,
    createdAt: row.created_at,
  }
}

function mapDocument(row: DocumentRow): Document {
  return {
    id: row.id,
    organizationId: row.organization_id,
    categoryId: row.category?.id ?? null,
    name: row.title,
    description: row.description,
    fileName: row.file_name,
    fileType: row.mime_type,
    fileSize: row.file_size,
    storagePath: row.file_url,
    version: Number.parseFloat(row.version) || 1,
    parentId: null,
    downloadCount: 0,
    uploadedBy: '',
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    deletedAt: null,
    category: row.category
      ? {
          id: row.category.id,
          organizationId: row.organization_id,
          name: row.category.name,
          description: null,
          parentId: null,
          sortOrder: 0,
          createdAt: row.created_at,
        }
      : undefined,
  }
}

export function createSupabaseDocumentRepository(client: SupabaseClient): DocumentRepository {
  return {
    async listVisible(context: TenantContext, { limit, categoryId }: DocumentListInput) {
      let query = client
        .from('documents')
        .select('id, organization_id, title, description, file_url, file_name, file_size, mime_type, version, created_at, updated_at, category:document_categories(id, name)')
        .eq('organization_id', context.organizationId)
        .order('created_at', { ascending: false })
        .limit(Math.max(1, Math.min(limit, 100)))

      if (categoryId) query = query.eq('category_id', categoryId)

      const { data, error } = await query
      if (error) throw error
      return (data as unknown as DocumentRow[]).map(mapDocument)
    },

    async listCategories(context: TenantContext) {
      const { data, error } = await client
        .from('document_categories')
        .select('id, organization_id, name, description, created_at')
        .eq('organization_id', context.organizationId)
        .order('name', { ascending: true })

      if (error) throw error
      return (data as unknown as CategoryRow[]).map(mapCategory)
    },

    async createCategory(context: TenantContext, name: string) {
      const { data, error } = await client
        .rpc('upsert_document_category', { p_organization_id: context.organizationId, p_name: name })
        .single()

      if (error) throw error
      return mapCategory(data as unknown as CategoryRow)
    },

    async getDownloadUrl(_context: TenantContext, storagePath: string) {
      // Documents uploaded before the private bucket stored a full URL instead of an object path.
      if (storagePath.startsWith('http://') || storagePath.startsWith('https://')) return storagePath

      const { data, error } = await client.storage.from(DOCUMENTS_BUCKET).createSignedUrl(storagePath, SIGNED_URL_SECONDS)
      if (error) throw error
      if (!data?.signedUrl) throw new Error('No se pudo generar el enlace de descarga.')
      return data.signedUrl
    },

    async create(context: TenantContext, input: CreateDocumentInput) {
      const { data, error } = await client
        .rpc('create_document', {
          p_organization_id: context.organizationId,
          p_title: input.title,
          p_file_url: input.fileUrl,
          p_file_name: input.fileName,
          p_file_size: input.fileSize,
          p_mime_type: input.mimeType,
          p_category_id: input.categoryId ?? null,
          p_description: input.description ?? null,
          p_version: input.version ?? '1.0',
          p_is_public: input.isPublic ?? false,
        })
        .single()

      if (error) throw error
      const row = data as Record<string, unknown>
      return mapDocument({
        id: row.id as UUID,
        organization_id: row.organization_id as UUID,
        title: row.title as string,
        description: (row.description as string | null) ?? null,
        file_url: row.file_url as string,
        file_name: row.file_name as string,
        file_size: row.file_size as number,
        mime_type: row.mime_type as string,
        version: row.version as string,
        created_at: row.created_at as string,
        updated_at: row.updated_at as string,
        category: null,
      })
    },

    async createSigned(context: TenantContext, input: CreateSignedDocumentInput) {
      const { data, error } = await client
        .rpc('create_signed_document', {
          p_organization_id: context.organizationId,
          p_title: input.title,
          p_file_url: input.fileUrl,
          p_file_name: input.fileName,
          p_file_size: input.fileSize,
          p_method: input.method,
          p_signer_name: input.signerName,
          p_file_sha256: input.fileSha256,
          p_category_id: input.categoryId ?? null,
          p_description: input.description ?? null,
          p_signer_position: input.signerPosition ?? null,
        })
        .single()

      if (error) throw error
      const row = data as Record<string, unknown>
      return mapDocument({
        id: row.id as UUID,
        organization_id: row.organization_id as UUID,
        title: row.title as string,
        description: (row.description as string | null) ?? null,
        file_url: row.file_url as string,
        file_name: row.file_name as string,
        file_size: row.file_size as number,
        mime_type: row.mime_type as string,
        version: row.version as string,
        created_at: row.created_at as string,
        updated_at: row.updated_at as string,
        category: null,
      })
    },

    async delete(context: TenantContext, documentId: UUID) {
      if (!context.membershipId) throw new Error('Membership is required to delete a document')

      const { error } = await client.rpc('delete_document', {
        p_id: documentId,
        p_organization_id: context.organizationId,
      })
      if (error) throw error
    },
  }
}
